// =====================================================
// DASHBOARD STATS — Conversion & traçabilité commerciale (RUN 3)
// =====================================================
// Section « Conversion & traçabilité commerciale » du cadrage 07/07. Fonctions
// pures, testables en vitest, sans dépendance React. Réutilise les prédicats de
// nomenclature (source de vérité RÉELLE desertours-dev) et `resolveWindow`
// (piloté par le filtre Période comme les KPI globaux).
//
// TROIS livrables :
//   1. Entonnoir de conversion (dédoublé) : BPI → PI, PI → ID, + global BPI → ID.
//   2. Tableau commerciaux : par assignation (crews/contacts.assigned_to), avec
//      BPI traités / ID obtenus / Taux + sous-stats dépliables.
//   3. Taux édition N : annulation (ID résiliées) + report (basculés édition N+1).
//
// ── MAPPING « commercial = assignation » (établi contre le schéma réel) ──────
//   Le CRM n'a pas d'entité « commercial » : c'est l'ASSIGNATION qui fait foi.
//   - ID obtenus / PI  → crews.assigned_to (le seul niveau où l'assignation vit).
//   - BPI traités      → payment_cases scope=bpi paid, rattachés au commercial
//                        via crew_id → crews.assigned_to (les crews sont créés à
//                        la validation du BPI, cf. migration 00103). Un BPI sans
//                        crew_id tombe dans « Non assigné ».
//   - Appels / ID      → timeline_events call_logged, attribués par created_by
//                        (le commercial qui a consigné l'appel).
//
// ── DÉNOMINATEUR de conversion (source unique = mêmes comptes que les KPI) ────
//   BPI = payment_cases paid (carte KPI BPI), PI/ID = crews (cartes KPI PI/ID).
//   Les taux réutilisent EXACTEMENT ces comptes → cohérence stricte avec les
//   cartes affichées. ⚠️ Sur le seed courant PI (crews) > BPI (payment_cases) →
//   le taux BPI→PI peut dépasser 100 % : ce n'est pas un bug d'affichage mais un
//   écart réel de saisie (des équipages ont atteint la pré-inscription sans BPI
//   payé enregistré). Surfacé tel quel + note d'avertissement dans la carte.

import { isCancellationCrew, isIdCrew, isPiCrew, isPostponementCrew } from './nomenclature';
import { inWindow } from './periods';
import type { DateWindow, EditionRef, RawCrew, RawPayment, RawRows, RawUser } from './types';

const DAY = 86_400_000;

// ---------------------------------------------------------------------------
// Valeur de taux (fraction 0..1) avec Δ N-1 en POINTS de pourcentage
// ---------------------------------------------------------------------------

/**
 * Un taux = numerator/denominator (fraction). `value` null si denominator = 0
 * (→ « N/A »). Le Δ N-1 s'exprime en POINTS (value − prevValue), pas en %.
 */
export interface RateValue {
  /** Fraction 0..1 (peut dépasser 1 si num > den — écart de saisie). null si den=0. */
  value: number | null;
  numerator: number;
  denominator: number;
  /** Fraction N-1, null si indisponible (pas d'édition N-1 ou denom N-1 = 0). */
  prevValue: number | null;
  /** Δ en points de pourcentage (value−prevValue)×100. null si l'un manque. */
  deltaPts: number | null;
  /** true quand la comparaison N-1 est indisponible. */
  n1Unavailable: boolean;
}

/** Construit un taux N (+ N-1 optionnel) avec Δ en points. */
export function buildRate(
  numerator: number,
  denominator: number,
  prev?: { numerator: number; denominator: number } | null
): RateValue {
  const value = denominator > 0 ? numerator / denominator : null;
  const prevValue = prev && prev.denominator > 0 ? prev.numerator / prev.denominator : null;
  const n1Unavailable = prevValue == null;
  const deltaPts = value != null && prevValue != null ? (value - prevValue) * 100 : null;
  return { value, numerator, denominator, prevValue, deltaPts, n1Unavailable };
}

// ---------------------------------------------------------------------------
// Compteurs scopés (édition + fenêtre) — réutilisent les prédicats nomenclature
// ---------------------------------------------------------------------------

/** BPI payés d'une édition dans la fenêtre (created_at). = carte KPI BPI. */
function bpiPaid(rows: RawRows, editionId: string, w: DateWindow): RawPayment[] {
  return rows.bpiPayments.filter(
    (p) => p.product_id === editionId && p.status === 'paid' && inWindow(p.created_at, w)
  );
}

/** Crews d'une édition dans la fenêtre (created_at), passés par un filtre statut. */
function crewsIn(
  rows: RawRows,
  editionId: string,
  w: DateWindow,
  pred: (c: RawCrew) => boolean
): RawCrew[] {
  return rows.crews.filter(
    (c) => c.product_id === editionId && pred(c) && inWindow(c.created_at, w)
  );
}

// ---------------------------------------------------------------------------
// 1. Entonnoir de conversion (dédoublé + global)
// ---------------------------------------------------------------------------

export interface ConversionFunnel {
  /** Global : ID / BPI (le grand chiffre de la carte). */
  bpiToId: RateValue;
  /** Étape 1 : PI / BPI (taux de transformation). Peut dépasser 100 % (cf. FLAG). */
  bpiToPi: RateValue;
  /** Étape 2 : ID / PI (taux de conversion pré → déf). */
  piToId: RateValue;
}

export interface ConversionParams {
  rows: RawRows;
  edition: EditionRef;
  /** Fenêtre N = plage de dates sélectionnée. */
  window: DateWindow;
  /** Fenêtre N-1 = même plage décalée d'un an. */
  prevWindow: DateWindow;
}

/** Comptes (bpi, pi, id) d'une édition sur une fenêtre. */
function funnelCounts(rows: RawRows, editionId: string, w: DateWindow) {
  return {
    bpi: bpiPaid(rows, editionId, w).length,
    pi: crewsIn(rows, editionId, w, isPiCrew).length,
    id: crewsIn(rows, editionId, w, isIdCrew).length,
  };
}

/**
 * Entonnoir de conversion pour l'édition N, avec comparaison N-1 = même plage
 * décalée d'un an sur l'édition N-1 appariée (RETOUCHE 1).
 */
export function computeConversionFunnel({
  rows,
  edition,
  window: w,
  prevWindow: wPrev,
}: ConversionParams): ConversionFunnel {
  const n = funnelCounts(rows, edition.id, w);
  const prevId = edition.prevEditionId;
  const p = prevId ? funnelCounts(rows, prevId, wPrev) : null;

  return {
    bpiToId: buildRate(n.id, n.bpi, p ? { numerator: p.id, denominator: p.bpi } : null),
    bpiToPi: buildRate(n.pi, n.bpi, p ? { numerator: p.pi, denominator: p.bpi } : null),
    piToId: buildRate(n.id, n.pi, p ? { numerator: p.id, denominator: p.pi } : null),
  };
}

// ---------------------------------------------------------------------------
// 2. Tableau commerciaux (par assignation) + sous-stats dépliables
// ---------------------------------------------------------------------------

export interface CommercialSubStats {
  bpiToPi: RateValue;
  piToId: RateValue;
  /** Délai moyen BPI payé → équipage (jours), null si non calculable. */
  delaiMoyenJours: number | null;
  /** Appels consignés / ID obtenu (ratio), null si aucun ID. */
  appelsParId: number | null;
}

export interface CommercialRow {
  /** users.id, ou null pour la ligne « Non assigné ». */
  userId: string | null;
  name: string;
  bpiTraites: number;
  idObtenus: number;
  /** ID / BPI du commercial. */
  taux: RateValue;
  sub: CommercialSubStats;
}

/** Nom d'affichage d'un utilisateur (full_name sinon email). */
function userName(u: RawUser): string {
  return u.full_name?.trim() || u.email;
}

/** Clé d'assignation d'un BPI : crew.assigned_to (prioritaire) sinon null. */
function bpiAssignee(p: RawPayment, crewAssignee: Map<string, string | null>): string | null {
  if (p.crew_id && crewAssignee.has(p.crew_id)) return crewAssignee.get(p.crew_id) ?? null;
  return null;
}

/**
 * Construit une ligne commercial pour un bucket d'assignation donné.
 * @param userId null = bucket « Non assigné ».
 */
function buildCommercialRow(
  userId: string | null,
  name: string,
  bpisOfUser: RawPayment[],
  crewsOfUser: RawCrew[],
  callsOfUser: number,
  crewCreatedById: Map<string, string>
): CommercialRow {
  const bpiTraites = bpisOfUser.length;
  const piCount = crewsOfUser.filter(isPiCrew).length;
  const idCount = crewsOfUser.filter(isIdCrew).length;

  // Délai moyen : (crew.created_at − payment.created_at) en jours, sur les BPI
  // rattachés à un crew (proxy « délai de conversion BPI → équipage » ; le vrai
  // horodatage de passage en ID n'existe pas en base — cf. FLAG RUN 1).
  const delais: number[] = [];
  for (const p of bpisOfUser) {
    if (!p.crew_id) continue;
    const crewCreated = crewCreatedById.get(p.crew_id);
    if (!crewCreated) continue;
    const d = (new Date(crewCreated).getTime() - new Date(p.created_at).getTime()) / DAY;
    if (isFinite(d) && d >= 0) delais.push(d);
  }
  const delaiMoyenJours =
    delais.length > 0 ? delais.reduce((a, b) => a + b, 0) / delais.length : null;

  return {
    userId,
    name,
    bpiTraites,
    idObtenus: idCount,
    taux: buildRate(idCount, bpiTraites),
    sub: {
      bpiToPi: buildRate(piCount, bpiTraites),
      piToId: buildRate(idCount, piCount),
      delaiMoyenJours,
      // « Appels / ID » n'a de sens que pour un commercial identifié (les appels
      // sont attribués par created_by) → null pour la ligne « Non assigné ».
      appelsParId: userId != null && idCount > 0 ? callsOfUser / idCount : null,
    },
  };
}

/**
 * Tableau des commerciaux (une ligne par utilisateur ayant une activité +
 * « Non assigné »), trié par ID obtenus décroissant. Piloté par Période/édition.
 * Réel ≈ 25 personnes → le composant gère le repli + scroll.
 */
export function computeCommercialRows({
  rows,
  edition,
  window: w,
}: ConversionParams): CommercialRow[] {
  const crews = crewsIn(rows, edition.id, w, () => true);
  const bpis = bpiPaid(rows, edition.id, w);

  // Assignation par crew (pour rattacher les BPI) + date de création par crew.
  const crewAssignee = new Map<string, string | null>();
  const crewCreatedById = new Map<string, string>();
  crews.forEach((c) => {
    crewAssignee.set(c.id, c.assigned_to ?? null);
    crewCreatedById.set(c.id, c.created_at);
  });

  // Appels par utilisateur (created_by), sur la même fenêtre.
  const callsByUser = new Map<string, number>();
  (rows.calls ?? []).forEach((call) => {
    if (call.created_by && inWindow(call.created_at, w)) {
      callsByUser.set(call.created_by, (callsByUser.get(call.created_by) ?? 0) + 1);
    }
  });

  // Regroupe crews et BPI par assignation (clé null = « Non assigné »).
  const pushGrouped = <T>(map: Map<string | null, T[]>, k: string | null, v: T) => {
    const arr = map.get(k);
    if (arr) arr.push(v);
    else map.set(k, [v]);
  };
  const crewsByUser = new Map<string | null, RawCrew[]>();
  crews.forEach((c) => pushGrouped(crewsByUser, c.assigned_to ?? null, c));
  const bpisByUser = new Map<string | null, RawPayment[]>();
  bpis.forEach((p) => pushGrouped(bpisByUser, bpiAssignee(p, crewAssignee), p));

  const usersById = new Map((rows.users ?? []).map((u) => [u.id, u]));
  const keys = new Set<string | null>([...crewsByUser.keys(), ...bpisByUser.keys()]);

  const rowsOut: CommercialRow[] = [];
  keys.forEach((k) => {
    const name =
      k == null
        ? 'Non assigné'
        : usersById.get(k)
          ? userName(usersById.get(k)!)
          : 'Utilisateur inconnu';
    rowsOut.push(
      buildCommercialRow(
        k,
        name,
        bpisByUser.get(k) ?? [],
        crewsByUser.get(k) ?? [],
        k == null ? 0 : (callsByUser.get(k) ?? 0),
        crewCreatedById
      )
    );
  });

  // Tri : ID obtenus desc, puis BPI traités desc, « Non assigné » toujours en bas.
  return rowsOut.sort((a, b) => {
    if (a.userId == null) return 1;
    if (b.userId == null) return -1;
    if (b.idObtenus !== a.idObtenus) return b.idObtenus - a.idObtenus;
    return b.bpiTraites - a.bpiTraites;
  });
}

// ---------------------------------------------------------------------------
// 3. Taux édition N : annulation + report
// ---------------------------------------------------------------------------

export interface EditionRates {
  /** ID résiliées / cohorte ayant atteint l'ID. */
  annulation: RateValue;
  /** Équipages basculés sur l'édition suivante / cohorte ayant atteint l'ID. */
  report: RateValue;
}

/**
 * Cohorte « a atteint l'inscription définitive » = ID actifs + résiliés +
 * reportés (tous étaient ID avant de sortir). Sert de base aux deux taux.
 */
function idCohortCounts(rows: RawRows, editionId: string, w: DateWindow) {
  const active = crewsIn(rows, editionId, w, isIdCrew).length;
  const cancelled = crewsIn(rows, editionId, w, isCancellationCrew).length;
  const postponed = crewsIn(rows, editionId, w, isPostponementCrew).length;
  return { active, cancelled, postponed, base: active + cancelled + postponed };
}

/** Taux annulation / report de l'édition N, avec Δ N-1 (points). */
export function computeEditionRates({
  rows,
  edition,
  window: w,
  prevWindow: wPrev,
}: ConversionParams): EditionRates {
  const n = idCohortCounts(rows, edition.id, w);
  const prevId = edition.prevEditionId;
  const p = prevId ? idCohortCounts(rows, prevId, wPrev) : null;

  return {
    annulation: buildRate(
      n.cancelled,
      n.base,
      p ? { numerator: p.cancelled, denominator: p.base } : null
    ),
    report: buildRate(
      n.postponed,
      n.base,
      p ? { numerator: p.postponed, denominator: p.base } : null
    ),
  };
}

// ---------------------------------------------------------------------------
// Bundle pour le hook
// ---------------------------------------------------------------------------

export interface ConversionData {
  funnel: ConversionFunnel;
  commercials: CommercialRow[];
  editionRates: EditionRates;
}

export function computeConversion(params: ConversionParams): ConversionData {
  return {
    funnel: computeConversionFunnel(params),
    commercials: computeCommercialRows(params),
    editionRates: computeEditionRates(params),
  };
}
